import "./admin.css";
import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { get } from "../lib/api";
import { qrSvgDataUrl } from "../lib/qrcode";
import { useSession } from "../session";
import { toApiError } from "./common";

interface SiteQr {
  siteId: string;
  siteName: string;
  version: number;
  url: string;
}

type Sticker = { ok: true; qr: SiteQr; img: string } | { ok: false; siteId: string; error: string };

const printedDate = () =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(Date.now());

/** A4 sheet of QR stickers, two per row. Rendered outside the admin layout so it prints cleanly. */
export function StickerSheetPage() {
  const { me } = useSession();
  const [params] = useSearchParams();
  const ids = (params.get("ids") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const idsKey = ids.join(",");
  const [stickers, setStickers] = useState<Sticker[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const list = idsKey ? idsKey.split(",") : [];
    setStickers(null);
    Promise.all(
      list.map(async (id): Promise<Sticker> => {
        try {
          const qr = await get<SiteQr>(`/admin/sites/${encodeURIComponent(id)}/qr`);
          return { ok: true, qr, img: await qrSvgDataUrl(qr.url) };
        } catch (e) {
          return { ok: false, siteId: id, error: toApiError(e).message };
        }
      }),
    ).then((r) => !cancelled && setStickers(r));
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  if (me && me.user.role !== "admin") return <Navigate to="/" replace />;

  const business = me?.settings.businessName ?? "";
  const date = printedDate();
  const failed = stickers?.filter((s) => !s.ok) ?? [];

  return (
    <>
      <div className="sticker-toolbar no-print stack">
        <Link to="/admin/sites" className="adm-back">
          ← Sites
        </Link>
        <div className="adm-head">
          <div>
            <h1>QR stickers</h1>
            <div className="sub">
              Print at 100% scale (not “fit to page”) on A4. Each sticker is about 9 × 12 cm.
            </div>
          </div>
          <button className="btn primary" onClick={() => window.print()} disabled={!stickers?.length}>
            Print
          </button>
        </div>
        {!ids.length && (
          <div className="banner info">
            No sites chosen. Go to <Link to="/admin/sites">Sites</Link>, tick the ones you need and press
            “Print stickers”.
          </div>
        )}
        {failed.length > 0 && (
          <div className="banner danger">
            {failed.length === 1 ? "One sticker" : `${failed.length} stickers`} couldn't be loaded:{" "}
            {failed.map((f) => (!f.ok ? f.error : "")).join("; ")}
          </div>
        )}
        {ids.length > 0 && !stickers && <p className="muted">Preparing stickers…</p>}
      </div>
      {stickers?.some((s) => s.ok) && (
        <div className="sticker-sheet">
          {stickers.map((s) =>
            s.ok ? (
              <div className="sticker" key={s.qr.siteId}>
                <div className="biz">
                  <img src="/brand/nklean-logo-ink.png" alt={business} />
                </div>
                <div className="site">{s.qr.siteName}</div>
                <img className="qr" src={s.img} alt={`QR code for ${s.qr.siteName}`} />
                <div className="how">Open the NKLEAN Hub app and tap Clock in to scan this code</div>
                <div className="ver">
                  Code v{s.qr.version} · printed {date}
                </div>
              </div>
            ) : null,
          )}
        </div>
      )}
    </>
  );
}
